import React from 'react';
import { Button, EButtonType } from '../Button';
import { IconClose } from '../Icons';
import { searchGroups } from '../LocationSearchInput';
import { focusNextElement } from './focusNextElement';
import Input from './Input';
import MultiPlacesTag from './MultiPlacesTag';
import { matchAny, matchExact } from './matchers';
import PreSelectedTag from './PreSelectedTag';
import Suggestions from './Suggestions';
import Tag from './Tag';

const KEYS = {
  ENTER: 'Enter',
  TAB: 'Tab',
  BACKSPACE: 'Backspace',
  UP_ARROW: 'ArrowUp',
  UP_ARROW_COMPAT: 'Up',
  DOWN_ARROW: 'ArrowDown',
  DOWN_ARROW_COMPAT: 'Down',
};

const CLASS_NAMES = {
  root: 'react-tags',
  rootFocused: 'is-focused',
  selected: 'react-tags__selected',
  selectedTag: 'react-tags__selected-tag',
  selectedTagName: 'react-tags__selected-tag-name',
  search: 'react-tags__search',
  groups: 'react-tags__group',
  searchWrapper: 'react-tags__search-wrapper',
  searchInput: 'react-tags__search-input',
  suggestions: 'react-tags__suggestions',
  suggestionActive: 'is-active',
  suggestionDisabled: 'is-disabled',
  suggestionPrefix: 'react-tags__suggestion-prefix',
  rightInnerComponent: 'react-tags__right-inner-component',
};

function findMatchIndex(options, query) {
  return options.findIndex((option) => matchExact(query).test(option.name));
}

function pressDelimiter(this: any) {
  if (this.state.query.length >= (this.props.minQueryLength || 2)) {
    // Check if the user typed in an existing suggestion.
    const match = findMatchIndex(this.state.options, this.state.query);
    const index = this.state.index === -1 ? match : this.state.index;
    const tag = index > -1 ? this.state.options[index] : null;

    if (tag) {
      this.addTag(tag);
    } else if (this.props.allowNew) {
      this.addTag({ name: this.state.query });
    }
  }
}

function pressUpKey(this: any, e) {
  e.preventDefault();

  // if first item, cycle to the bottom
  const size = this.state.options.length - 1;
  this.setState({ index: this.state.index <= 0 ? size : this.state.index - 1 });
}

function pressDownKey(this: any, e) {
  e.preventDefault();

  // if last item, cycle to top
  const size = this.state.options.length - 1;
  this.setState({ index: this.state.index >= size ? 0 : this.state.index + 1 });
}

function pressBackspaceKey(this: any, e) {
  // when backspace key is pressed and query is blank, delete the last tag
  if (!this.state.query.length) {
    if (this.props.preSelectedTag) {
      this.props.onDeleteCustomTag();
    } else {
      this.deleteTag(this.props.tags.length - 1);
    }
  }
}

function defaultSuggestionsFilter(item, query) {
  const regexp = matchAny(query);
  return regexp.test(item.name);
}

function getOptions(props, state) {
  let options;

  if (props.suggestionsTransform) {
    options = props.suggestionsTransform(state.query, props.suggestions);
  } else {
    const filterFunc = props.suggestionsFilter || defaultSuggestionsFilter;
    options = props.suggestions.filter((item) => filterFunc(item, state.query));
  }

  options = options.slice(0, props.maxSuggestionsLength || 6);

  if (props.allowNew && props.newTagText && findMatchIndex(options, state.query) === -1) {
    options.push({ id: 0, name: state.query, prefix: props.newTagText, disableMarkIt: true });
  } else if (props.noSuggestionsText && options.length === 0) {
    options.push({ id: 0, name: props.noSuggestionsText, disabled: true, disableMarkIt: true });
  }

  return options;
}

interface Props {
  id?: string;
  tags: any;
  preSelectedTag?: any;
  hideImportedTag?: boolean;
  setHideImportedTag?: any;
  placeholderText?: string;
  ariaLabelText?: string;
  removeButtonText?: string;
  noSuggestionsText?: string;
  newTagText?: string;
  suggestions?: any;
  suggestionsFilter?: any;
  suggestionsTransform?: any;
  autoresize?: boolean;
  delimiters?: string[];
  onDeleteCustomTag?: () => void;
  onDelete?: (i?: any, b?: any) => void;
  onAddition?: (i?: any, b?: any) => void;
  clearAll?: any;
  onChange?: any;
  onInput?: any;
  onFocus?: any;
  onBlur?: any;
  onValidate?: any;
  minQueryLength?: number;
  maxSuggestionsLength?: number;
  classNames?: any;
  allowNew?: boolean;
  allowBackspace?: boolean;
  addOnBlur?: boolean;
  tagComponent?: any;
  suggestionComponent?: any;
  inputAttributes?: any;
  optionsGroups?: any;
  isLoading?: boolean;
  initialScreen?: boolean;
  rightInnerComponent?: any;
  onTagClick?: any;
  highlightedTags?: string[];
  rightSuggestionsComponent: any;
  isShowGroupDescription: boolean;
  kilometersLabel: string | React.ReactNode;
  metersLabel: string | React.ReactNode;
  radiusLabel: string | React.ReactNode;
}

interface State {
  query: string;
  focused: boolean;
  index: number;
  options?: any[];
}

class ReactTags extends React.Component<Props, State> {
  private inputEventHandlers: {
    onBlur: () => void;
    onKeyDown: (e) => void;
    onChange: () => void;
    onInput: (e) => void;
    onFocus: () => void;
  };
  private container: any;
  private input: any;

  constructor(props) {
    super(props);

    this.state = {
      query: '',
      focused: false,
      index: -1,
    };

    this.inputEventHandlers = {
      // Provide a no-op function to the input component to avoid warnings
      // <https://github.com/i-like-robots/react-tags/issues/135>
      // <https://github.com/facebook/react/issues/13835>
      onChange: this.onChange.bind(this),
      onBlur: this.onBlur.bind(this),
      onFocus: this.onFocus.bind(this),
      onInput: this.onInput.bind(this),
      onKeyDown: this.onKeyDown.bind(this),
    };

    this.container = React.createRef();
    this.input = React.createRef();
  }

  static getDerivedStateFromProps(props, state) {
    if (state.prevQuery !== state.query || state.prevSuggestions !== props.suggestions) {
      return {
        prevQuery: state.query,
        prevSuggestions: props.suggestions,
        options: getOptions(props, state),
      };
    }

    return null;
  }

  handleClickOutside = (event) => {
    if (this.container.current && !this.container.current.contains(event.target)) {
      this.setState({ focused: false, index: -1 });
      this.clearInput();
    }
  };

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside, true);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  }

  onInput(e) {
    const query = e.target.value;

    if (this.props.onInput) {
      this.props.onInput(query);
    }

    // NOTE: This test is a last resort for soft keyboards and browsers which do not
    // support `KeyboardEvent.key`.
    // <https://bugs.chromium.org/p/chromium/issues/detail?id=763559>
    // <https://bugs.chromium.org/p/chromium/issues/detail?id=118639>
    const delimiters = this.props.delimiters || [KEYS.ENTER, KEYS.TAB];
    if (query.length === this.state.query.length + 1 && delimiters.indexOf(query.slice(-1)) > -1) {
      pressDelimiter.call(this);
    } else if (query !== this.state.query) {
      this.setState({ query });
    }
  }

  onKeyDown(e) {
    // when one of the terminating keys is pressed, add current query to the tags
    const delimiters = this.props.delimiters || [KEYS.ENTER, KEYS.TAB];
    if (delimiters.indexOf(e.key) > -1) {
      if (this.state.query || this.state.index > -1) {
        e.preventDefault();
      }

      pressDelimiter.call(this);
    }

    // when backspace key is pressed and query is blank, delete the last tag
    if (e.key === KEYS.BACKSPACE && (this.props.allowBackspace || true)) {
      pressBackspaceKey.call(this, e);
    }

    if (e.key === KEYS.UP_ARROW || e.key === KEYS.UP_ARROW_COMPAT) {
      pressUpKey.call(this, e);
    }

    if (e.key === KEYS.DOWN_ARROW || e.key === KEYS.DOWN_ARROW_COMPAT) {
      pressDownKey.call(this, e);
    }
  }

  onClick(e) {
    if (document.activeElement !== e.target) {
      this.focusInput();
    }
  }

  onChange() {
    if (this.props.onChange) {
      this.props.onChange();
    }
  }

  onBlur() {
    if (this.props.onBlur) {
      this.props.onBlur();
    }

    if (this.props.addOnBlur) {
      pressDelimiter.call(this);
    }
  }

  onFocus() {
    this.setState({ focused: true });

    if (this.props.onFocus) {
      this.props.onFocus();
    }
  }

  onDeleteTag(index, event) {
    // Because we'll destroy the element with cursor focus we need to ensure
    // it does not get lost and move it to the next interactive element
    if (this.container.current) {
      focusNextElement(this.container.current, event.currentTarget);
    }

    this.deleteTag(index);
  }

  addTag(tag) {
    if (tag.disabled) {
      return;
    }

    if (typeof this.props.onValidate === 'function' && !this.props.onValidate(tag)) {
      return;
    }

    this.props.onAddition(tag);

    this.clearInput();
  }

  deleteTag(i) {
    this.props.onDelete(i);
  }

  clearInput() {
    this.blugInput();
    if (this.props.onBlur) {
      this.props.onBlur();
    }

    this.props.onInput('');
    this.setState({
      query: '',
      index: -1,
      focused: false,
    });
  }

  focusInput() {
    // @ts-ignore
    if (this.input.current && this.input.current.input.current) {
      // @ts-ignore
      this.input.current.input.current.focus();
    }
  }

  blugInput() {
    // @ts-ignore
    if (this.input.current && this.input.current.input.current) {
      // @ts-ignore
      this.input.current.input.current.blur();
    }
  }

  clearAll() {
    this.clearInput();
    this.props.onInput('');
    this.props.clearAll();
  }

  render() {
    const TagComponent = this.props.tagComponent || Tag;

    const expanded = this.state.focused;
    const importedIds = this.props.tags.filter(
      (tag) => tag?.data?.types.includes('product_id') && tag?.data?.types.includes('imported'),
    );
    const classNames = Object.assign({}, CLASS_NAMES, this.props.classNames);
    const rootClassNames = [classNames.root];

    this.state.focused && rootClassNames.push(classNames.rootFocused);
    this.props.preSelectedTag && rootClassNames.push('has-pre-selected');
    this.props.hideImportedTag && importedIds.length > 25 && rootClassNames.push('collapse-imported-tag');

    return (
      <div ref={this.container} className={rootClassNames.join(' ')}>
        {this.props.tags.length > 0 && (
          <div className={classNames.selected} aria-relevant='additions removals' aria-live='polite'>
            {this.props.hideImportedTag && importedIds.length > 25 && (
              <MultiPlacesTag
                classNames={classNames}
                tagIcon={searchGroups['ids']}
                totolIds={importedIds.length}
                onDelete={(e) => {
                  importedIds.map((tag, i) => {
                    this.onDeleteTag(i, e);
                  });
                }}
                onClick={() => {
                  this.props.setHideImportedTag(false);
                  importedIds.map((tag, i) => {
                    if (tag?.data?.types.includes('imported')) {
                      tag?.data?.types.splice(
                        tag?.data?.types.indexOf('product_id') && tag?.data?.types.indexOf('imported'),
                        1,
                      );
                      this.props.onChange(tag);
                    }
                  });
                }}
              />
            )}
            {this.props.tags.map((tag, i) => (
              <TagComponent
                key={i}
                tag={tag}
                removeButtonText={this.props.removeButtonText || 'Click to remove tag'}
                classNames={classNames}
                onDelete={this.onDeleteTag.bind(this, i)}
                onClick={() => (tag.urn ? this.props.onTagClick(tag.urn) : null)}
                onChange={(newTag) => {
                  this.props.onChange(newTag);
                }}
                isHighlighted={this.props.highlightedTags?.includes(tag.urn)}
                tagIcon={this.props.optionsGroups.filter((group) => group.id === tag.data?.context)[0]?.icon}
                kilometersLabel={this.props.kilometersLabel}
                metersLabel={this.props.metersLabel}
                radiusLabel={this.props.radiusLabel}
              />
            ))}
          </div>
        )}

        <div
          className={classNames.search + (this.props.tags.length > 0 ? ' clear-button' : '')}
          onClick={this.onClick.bind(this)}
        >
          {this.props?.preSelectedTag && (
            <div className={classNames.selected}>
              <PreSelectedTag
                key='custom-tag'
                isPreSelectedTag={true}
                tag={this.props?.preSelectedTag}
                removeButtonText={this.props.removeButtonText || 'Click to remove tag'}
                classNames={classNames}
                onDelete={() => {
                  this.props.onDeleteCustomTag();
                  this.clearInput();
                }}
                onChange={(newTag) => {
                  this.props.onChange(newTag);
                }}
                addressInputComponent={
                  <Input
                    {...this.state}
                    id={this.props.id || 'ReactTags'}
                    ref={this.input}
                    isFocused={true}
                    inputType={
                      ['id', 'postal_code'].includes(this.props?.preSelectedTag?.data?.context) ? 'number' : 'text'
                    }
                    classNames={classNames}
                    inputAttributes={this.props.inputAttributes || {}}
                    inputEventHandlers={this.inputEventHandlers}
                    autoresize={this.props.autoresize || true}
                    expanded={expanded}
                    placeholderText={''}
                    ariaLabelText={this.props.ariaLabelText}
                  />
                }
                tagIcon={
                  this.props.optionsGroups.filter((group) => group.id === this.props?.preSelectedTag?.data?.context)[0]
                    ?.icon
                }
                kilometersLabel={this.props.kilometersLabel}
                metersLabel={this.props.metersLabel}
                radiusLabel={this.props.radiusLabel}
              />
              <Button
                buttonType={EButtonType.BasicLink}
                className={'d-flex align-items-center mt-0 clear-input-search'}
                isSvgFill={false}
                title='click to remove all'
                onClick={() => {
                  this.clearAll();
                }}
              >
                <IconClose />
              </Button>
            </div>
          )}
          {!this.props?.preSelectedTag && (
            <Input
              {...this.state}
              id={this.props.id || 'ReactTags'}
              ref={this.input}
              isFocused={this.state.focused}
              classNames={classNames}
              inputAttributes={this.props.inputAttributes || {}}
              inputEventHandlers={this.inputEventHandlers}
              autoresize={this.props.autoresize || true}
              expanded={expanded}
              placeholderText={this.props.placeholderText || 'Add new tag'}
              ariaLabelText={this.props.ariaLabelText}
              rightInnerComponent={
                <div className='d-flex align-items-center justify-content-center gap-3'>
                  {this.props.rightInnerComponent}
                  {this.props.tags.length > 0 && (
                    <Button
                      buttonType={EButtonType.BasicLink}
                      className={'d-flex align-items-center mt-0 clear-input-search'}
                      isSvgFill={false}
                      title='click to remove all'
                      onClick={() => {
                        this.clearAll();
                      }}
                    >
                      <IconClose />
                    </Button>
                  )}
                </div>
              }
            />
          )}
          {expanded && (this.state.options.length || this.props.isLoading || this.props.initialScreen) ? (
            <Suggestions
              {...this.state}
              id={this.props.id || 'ReactTags'}
              classNames={classNames}
              expanded={expanded}
              addTag={this.addTag.bind(this)}
              optionsGroups={this.props.optionsGroups}
              initialScreen={this.props.initialScreen}
              isLoading={this.props.isLoading}
              suggestionComponent={this.props.suggestionComponent}
              rightSuggestionsComponent={this.props?.preSelectedTag ? null : this.props.rightSuggestionsComponent}
              isShowGroupDescription={this.props.isShowGroupDescription}
            />
          ) : null}
        </div>
      </div>
    );
  }
}

export default ReactTags;
